import { PERIOD_POLICY } from 'domain/projects/newProject/billing/constant';
import { BillingType, MilestoneType } from 'domain/shared/newProject';

type BillingPeriodType = keyof typeof PERIOD_POLICY;

export enum ScheduleItemStatus {
  PLANNED = 'PLANNED',
  DRAFT = 'DRAFT',
  IN_APPROVAL = 'IN_APPROVAL',
  CLARIFICATION_REQUESTED = 'CLARIFICATION_REQUESTED',
  APPROVED = 'APPROVED',
  SCHEDULE_ITEM_RELATED = 'SCHEDULE_ITEM_RELATED',
  SYSTEM_PENDING = 'SYSTEM_PENDING',
}

export const BILLING_STATUS_OPTIONS = {
  [ScheduleItemStatus.PLANNED]: 'Planned',
  [ScheduleItemStatus.IN_APPROVAL]: 'In Approval',
  [ScheduleItemStatus.CLARIFICATION_REQUESTED]: 'Clarification Requested',
  [ScheduleItemStatus.APPROVED]: 'Approved',
  [ScheduleItemStatus.DRAFT]: 'Draft',
  [ScheduleItemStatus.SYSTEM_PENDING]: 'Pending client approval',
};

export const SCHEDULE_BILLING_STATUS_OPTIONS = {
  [ScheduleItemStatus.PLANNED]: 'Planned',
  [ScheduleItemStatus.IN_APPROVAL]: 'In Approval',
  [ScheduleItemStatus.DRAFT]: 'Draft',
  [ScheduleItemStatus.CLARIFICATION_REQUESTED]: 'Clarification Requested',
  [ScheduleItemStatus.APPROVED]: 'Approved',
  [ScheduleItemStatus.SCHEDULE_ITEM_RELATED]: 'Milestone Related',
  [ScheduleItemStatus.SYSTEM_PENDING]: 'Pending client approval',
};

export const ScheduleItem = {
  MILESTONE: 'MILESTONE',
  CALENDAR_PERIOD: 'CALENDAR_PERIOD',
  CUSTOM_PERIOD: 'CUSTOM_PERIOD',
} as const;
interface CalendarPeriodScheduleItemRequest {
  id?: string | null;
  type: 'CALENDAR_PERIOD';
  sowId?: string;
  status?: ScheduleItemStatus;
  position?: string | null;
  invoiceDate?: string | null;
  periodMonth: string;
  amount: number;
  amountId?: string | null;
  currency: string | null;
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  hours?: number | null;
}

interface CustomPeriodScheduleItemRequest {
  id?: string | null;
  type: 'CUSTOM_PERIOD';
  sowId?: string;
  invoiceDate: string | null;
  position?: string | null;
  startDate: string;
  endDate: string;
  amount: number;
  amountId?: string | null;
  currency: string | null;
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  hours?: number | null;
}

interface MilestoneScheduleItemRequest {
  id?: string | null;
  type: 'MILESTONE';
  sowId?: string;
  milestoneType: MilestoneType;
  milestoneName: string;
  amount: number;
  amountId?: string | null;
  currency: string | null;
  alias?: string;
  invoiceDate?: string | Date | null;
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  hours?: number | null;
  isCompleted?: boolean;
}

export type ScheduleItemDto =
  CalendarPeriodScheduleItemRequest | CustomPeriodScheduleItemRequest | MilestoneScheduleItemRequest;

export interface CreateBillingScheduleRequest {
  requestId?: string | null;
  projectId: string;
  accountId: string;
  billingType: BillingType;
  periodPolicy?: BillingPeriodType | null;
  sowStartDate?: string | null;
  sowEndDate?: string | null;
  invoiceDateBusinessRule?: string | null;
  scheduleItems: ScheduleItemDto[];
}

export interface ExistingScheduleItemUpdateRequest {
  id: string;
  sowId?: string;
  invoiceDate?: string | null;
  amount?: number;
  amountId?: string | null;
  currency?: string | null;
  milestoneName?: string;
}

export interface UpdateBillingScheduleRequest {
  existingItems: ExistingScheduleItemUpdateRequest[];
  newItems: ScheduleItemDto[];
  removedItemIds: string[];
}

interface CalendarPeriodScheduleItemResponse {
  id: string;
  sowId: string;
  status: ScheduleItemStatus;
  position: string;
  invoiceDate: string | null;
  amount: number | null;
  currency: string | null;
  type: 'CALENDAR_PERIOD';
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  periodMonth: string;
  hours: number | null;
}

interface CustomPeriodScheduleItemResponse {
  id: string;
  sowId: string;
  status: ScheduleItemStatus;
  position: string;
  invoiceDate: string | null;
  amount: number | null;
  currency: string | null;
  type: 'CUSTOM_PERIOD';
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  startDate: string;
  endDate: string;
  hours: number | null;
}

export interface MilestoneScheduleItemResponse {
  id: string;
  sowId: string;
  status: ScheduleItemStatus;
  position: string;
  invoiceDate: string | null;
  amount: number | null;
  currency: string | null;
  type: 'MILESTONE' | 'CHANGE_REQUEST';
  billingRequestId?: string | null;
  billingRequestDate?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  milestoneType: MilestoneType;
  relatedItem?: string;
  alias?: string;
  milestoneName: string;
  changeRequestAlias?: string;
  isCompleted?: boolean;
  hours?: number | null;
}

export type BillingScheduleItemResponse =
  CalendarPeriodScheduleItemResponse | CustomPeriodScheduleItemResponse | MilestoneScheduleItemResponse;

interface CalendarPeriodBillingScheduleResponse {
  id: string;
  projectId: string;
  accountId: string;
  billingType: BillingType.PERIODIC_PREPAID | BillingType.PERIODIC_POSTPAID;
  periodPolicy: BillingPeriodType;
  scheduleItems: CalendarPeriodScheduleItemResponse[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface CustomPeriodBillingScheduleResponse {
  id: string;
  projectId: string;
  accountId: string;
  billingType: BillingType.PERIODIC_PREPAID | BillingType.PERIODIC_POSTPAID;
  periodPolicy: BillingPeriodType;
  scheduleItems: CustomPeriodScheduleItemResponse[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface MilestoneBillingScheduleResponse {
  id: string;
  projectId: string;
  accountId: string;
  billingType: BillingType.MILESTONE;
  periodPolicy: null;
  scheduleItems: MilestoneScheduleItemResponse[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type BillingScheduleResponse =
  CalendarPeriodBillingScheduleResponse | CustomPeriodBillingScheduleResponse | MilestoneBillingScheduleResponse;

export interface BillingScheduleSearchRequest {
  pageNumber?: number;
  pageSize: number;
  filter: {
    projectId?: {
      eq: string;
    };
    id?: {
      eq: string;
    };
    [key: string]: any;
  };
}

export interface BillingScheduleSearchResponse {
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  result: Array<BillingScheduleResponse>;
}

export interface BillingScheduleSensitiveItem {
  id: string;
  sowId: string;
  amount: number;
  currency: string;
  invoiceDate: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface BillingScheduleSensitiveSearchResponse {
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  result:
    | Array<{
        id: string;
        projectId: string;
        accountId: string;
        billingType: BillingType;
        scheduleItems: BillingScheduleSensitiveItem[];
        createdAt: string;
        createdBy: string;
        updatedAt: string | null;
        updatedBy: string | null;
      }>
    | [];
}

export enum BillingRequestStatus {
  DRAFT = 'DRAFT',
  IN_APPROVAL = 'IN_APPROVAL',
  REJECTED = 'REJECTED',
  BILLED = 'BILLED',
}

export interface CreateBilledItem {
  scheduleItemId: string;
  name: string;
  amount: number;
  currency: string;
  hours?: number;
  plannedRegularHours?: number;
  plannedOvertimeHours?: number;
  previousPeriodAdjustmentHours?: number;
}

export interface UpdateBilledItem {
  scheduleItemId?: string;
  name: string;
  amount: number;
  currency: string;
  hours?: number;
  plannedRegularHours?: number;
  plannedOvertimeHours?: number;
  previousPeriodAdjustmentHours?: number;
}

export interface CreateBillingRequestPayload {
  note: string;
  billedItems: CreateBilledItem[];
  serviceDetailsArtifactId?: string;
  reimbursementDocumentationArtifactIds?: string[];
  startDate?: string;
  endDate?: string;
  status: BillingRequestStatus;
}

export interface MigrateBillingRequestPayload {
  note: string;
  billedItems: CreateBilledItem[];
  billingRequestArtifactId?: string;
  billingRequestDate: string;
}
export interface UpdateMigratedBillingRequestPayload {
  note: string;
  billedItems: Array<CreateBilledItem & { id: string }>;
  billingRequestArtifactId?: string;
  billingRequestDate: string;
}

export interface UpdateBillingRequestPayload {
  note: string;
  billedItems: UpdateBilledItem[];
  serviceDetailsArtifactId?: string;
  reimbursementDocumentationArtifactIds?: string[];
  startDate?: string;
  endDate?: string;
  paidOn: string | null;
  status: BillingRequestStatus;
  rejectionReason?: string | null;
}

export interface ApproveBillingRequestPayload {
  serviceDetailsArtifactId?: string;
  startDate?: string;
  endDate?: string;
}

interface BillingRequestScheduleItem {
  type: 'CALENDAR_PERIOD' | 'CUSTOM_PERIOD' | 'MILESTONE' | 'CHANGE_REQUEST';
  id: string;
  sowId: string;
  status: ScheduleItemStatus;
  position: string;
  billingRequestId: string | null;
  hours: number | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  periodMonth?: string;
  startDate?: string;
  endDate?: string;
  milestoneType?: MilestoneType;
  milestoneName?: string;
  amount?: number | null;
  currency?: string | null;
  plannedRegularHours?: number | null;
  plannedOvertimeHours?: number | null;
  previousPeriodAdjustmentHours?: number | null;
}

export interface BillingRequestResponse {
  id: string;
  projectId: string;
  accountId: string;
  billingType: BillingType;
  periodPolicy: BillingPeriodType | null;
  scheduleItems: BillingRequestScheduleItem[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface BillingRequestItem {
  id: string;
  itemId: string;
  name: string;
  amount: number;
  currency: string;
  hours: number | null;
  plannedRegularHours?: number | null;
  plannedOvertimeHours?: number | null;
  previousPeriodAdjustmentHours?: number | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface BillingRequestSearchResultItem {
  id: string;
  projectName: string;
  projectManager: string;
  accountId: string;
  sowId: string;
  billingType: BillingType;
  note: string;
  serviceDetailsArtifactId?: string | null;
  reimbursementDocumentationArtifactIds?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
  asOfDate: string;
  paidOn: string | null;
  status: BillingRequestStatus;
  rejectionReason: string | null;
  items: BillingRequestItem[];
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  relatedDocuments?: string[];
}

export interface BillingScheduleItemsSearchRequest {
  itemsIds: string[];
}

interface UpdateInvoiceDatesItem {
  id: string;
  invoiceDate: string | null;
}

export interface UpdateInvoiceDatesPayload {
  items: UpdateInvoiceDatesItem[];
}

export interface BillingScheduleItemResult {
  type: 'MILESTONE' | 'CALENDAR_PERIOD' | 'CUSTOM_PERIOD' | 'CHANGE_REQUEST';
  id: string;
  sowId: string;
  status: ScheduleItemStatus | 'SCHEDULE_ITEM_RELATED';
  position: string;
  invoiceDate: string | null;
  amount: number | null;
  currency: string | null;
  billingRequestId: string | null;
  hours: number | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  milestoneType?: MilestoneType;
  alias?: string;
  milestoneName?: string;
  periodMonth?: string;
  startDate?: string;
  endDate?: string;
  relatedItem?: string;
  plannedRegularHours?: number | null;
  plannedOvertimeHours?: number | null;
  previousPeriodAdjustmentHours?: number | null;
}

export interface ApproveBillingData {
  items: BillingScheduleItemResult[];
  type: string | undefined;
  createdBy: string | undefined;
  createdAt: string | undefined;
  sowId: string | undefined;
  note: string | undefined;
  serviceDetailsArtifactId?: string | null;
  reimbursementDocumentationArtifactIds?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: BillingRequestStatus;
  rejectionReason?: string | null;
  project:
    | {
        name?: string;
        projectManager?: string;
        accountData?: {
          accountId?: string | number;
          sowId?: number;
        };
      }
    | undefined;
  relatedDocuments?: string[];
  budgetId?: number;
}
